import { Stack, Tooltip } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import ListItemText from '@mui/material/ListItemText';

import { fAPIDate } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';

import Label from 'src/components/label';

import { IFactoringCandidate } from 'src/types/cheque-factoring';

type Props = {
  row: IFactoringCandidate;
  selected: boolean;
  onSelectRow: VoidFunction;
  // Constraint violations of a picked cheque (admins only) — shown as chips.
  violations?: string[];
};

export default function CandidatesTableRow({ row, selected, onSelectRow, violations }: Props) {
  const {
    chequeNumber,
    payerName,
    payerAccountNumber,
    bank,
    chequeDate,
    amountInCents,
    debtorHeadroomInCents,
    debtorReturnWarning,
  } = row;

  return (
    <TableRow hover selected={selected}>
      <TableCell padding="checkbox">
        <Checkbox checked={selected} onClick={onSelectRow} />
      </TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
        <ListItemText
          primary={bank}
          secondary={chequeNumber}
          primaryTypographyProps={{ typography: 'body2' }}
          secondaryTypographyProps={{
            component: 'span',
            color: 'text.disabled',
            typography: 'caption',
          }}
        />

        {!!violations?.length && (
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
            {violations.map((reason) => (
              <Label key={reason} variant="soft" color="error">
                {reason}
              </Label>
            ))}
          </Stack>
        )}
      </TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
        <ListItemText
          primary={
            debtorReturnWarning ? (
              <Tooltip
                title={`${debtorReturnWarning.returnCount} prior return(s)${
                  debtorReturnWarning.lastReturnDate
                    ? `, last on ${fAPIDate(debtorReturnWarning.lastReturnDate)}`
                    : ''
                }`}
              >
                <span>
                  {payerName}{' '}
                  <Label variant="soft" color="warning">
                    ⚠ {debtorReturnWarning.returnCount} return(s)
                  </Label>
                </span>
              </Tooltip>
            ) : (
              payerName
            )
          }
          secondary={payerAccountNumber}
          primaryTypographyProps={{ typography: 'body2' }}
          secondaryTypographyProps={{
            component: 'span',
            color: 'text.disabled',
            typography: 'caption',
          }}
        />
      </TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
        {fAPIDate(chequeDate)}
      </TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        {fCurrencyRupees(amountInCents)}
      </TableCell>

      <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
        {fCurrencyRupees(debtorHeadroomInCents)}
      </TableCell>
    </TableRow>
  );
}
